import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Calendar, ArrowRight, Shield, Clock, Users, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';

function Landing() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate] = useState('');

  useEffect(() => {
    fetchRoutes();
    const today = new Date().toISOString().split('T')[0];
    setDate(today);
  }, []);

  const fetchRoutes = async () => {
    try {
      const response = await api.get('/routes');
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    }
  };

  const handleSearch = () => {
    if (!origin || !destination || !date) {
      toast.error('Please select origin, destination and date');
      return;
    }
    navigate(`/search?origin=${origin}&destination=${destination}&date=${date}`);
  };

  const cities = [...new Set(routes.flatMap(r => [r.origin, r.destination]))];

  return (
    <div className="min-h-screen bg-neutral-background">
      <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
              alt="Hasaka Tours & Travels"
              className="h-8 sm:h-10 w-auto"
            />
          </div>
          <div className="flex gap-2 sm:gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-slate-600 hover:text-primary font-medium text-xs sm:text-sm uppercase tracking-wider px-2 sm:px-4"
              data-testid="login-button"
            >
              Login
            </Button>
            <Button
              onClick={() => navigate('/signup')}
              className="bg-primary text-white hover:bg-primary-900 rounded-sm px-4 sm:px-8 py-2 sm:py-6 font-semibold tracking-wide uppercase text-xs sm:text-sm"
              data-testid="signup-button"
            >
              Sign Up
            </Button>
          </div>
        </div>
      </nav>

      <section className="grid grid-cols-1 lg:grid-cols-12 min-h-[500px] sm:min-h-[600px]">
        <div className="col-span-1 lg:col-span-7 px-6 sm:px-12 lg:px-20 py-12 sm:py-16 lg:py-24 flex flex-col justify-center">
          <div className="mb-4 sm:mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg" 
              alt="Hasaka Tours & Travels"
              className="h-20 sm:h-24 lg:h-32 w-auto mb-3 sm:mb-4"
            />
          </div>
          <h2 className="font-manrope font-bold text-3xl sm:text-4xl lg:text-6xl text-primary leading-tight mb-4 sm:mb-6" data-testid="hero-heading">
            Premium Electric Travel
            <br />
            Between Major Cities
          </h2>
          <p className="text-base sm:text-lg text-slate-600 font-public-sans mb-6 sm:mb-8 max-w-xl">
            Experience luxury and sustainability with our fleet of BYD eMax 7 electric vehicles. Travel comfortably between
            Vijayawada, Hyderabad, Visakhapatnam, Chennai, and Bengaluru with Hasaka Prime EV.
          </p>

          <div className="bg-white/95 backdrop-blur-md shadow-2xl p-4 sm:p-6 lg:p-8 rounded-sm border border-slate-200" data-testid="search-widget">
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2 uppercase tracking-wide">
                  From
                </label>
                <Select onValueChange={setOrigin} data-testid="origin-select">
                  <SelectTrigger className="rounded-none border-slate-300 focus:border-primary focus:ring-1 focus:ring-primary bg-slate-50 h-10 sm:h-12 text-sm sm:text-base">
                    <SelectValue placeholder="Select Origin" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2 uppercase tracking-wide">
                  To
                </label>
                <Select onValueChange={setDestination} data-testid="destination-select">
                  <SelectTrigger className="rounded-none border-slate-300 focus:border-primary focus:ring-1 focus:ring-primary bg-slate-50 h-10 sm:h-12 text-sm sm:text-base">
                    <SelectValue placeholder="Select Destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2 uppercase tracking-wide">
                  Date
                </label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="rounded-none border-slate-300 focus:border-primary focus:ring-1 focus:ring-primary bg-slate-50 h-10 sm:h-12 text-sm sm:text-base"
                  data-testid="date-input"
                />
              </div>
              <Button
                onClick={handleSearch}
                className="bg-secondary text-white hover:bg-secondary-600 rounded-sm px-6 sm:px-8 py-4 sm:py-6 font-semibold tracking-wide uppercase text-xs sm:text-sm w-full"
                data-testid="search-button"
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Search Schedules
              </Button>
            </div>
          </div>
        </div>
        <div
          className="hidden lg:block col-span-5 bg-cover bg-center min-h-[300px]"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/c2yrkoit_Screenshot_20260114_004056_Gallery.jpg')",
          }}
        />
      </section>

      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-8 lg:px-20 bg-white">
        <h3 className="font-manrope font-bold text-2xl sm:text-3xl lg:text-4xl text-primary text-center mb-8 sm:mb-12 lg:mb-16">Why Choose Hasaka Prime EV?</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          <div className="p-6 sm:p-8 border-l-4 border-l-secondary bg-white hover:-translate-y-1 transition-transform duration-300 shadow-sm" data-testid="feature-comfort">
            <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-secondary mb-4" />
            <h4 className="font-manrope font-bold text-lg sm:text-xl text-primary mb-3">Safety & Reliability</h4>
            <p className="text-sm sm:text-base text-slate-600 font-public-sans">
              All vehicles regularly maintained with certified electric drivetrains. Experienced chauffeurs ensure your safety throughout the journey.
            </p>
          </div>
          <div className="p-6 sm:p-8 border-l-4 border-l-secondary bg-white hover:-translate-y-1 transition-transform duration-300 shadow-sm" data-testid="feature-time">
            <Clock className="w-10 h-10 sm:w-12 sm:h-12 text-secondary mb-4" />
            <h4 className="font-manrope font-bold text-lg sm:text-xl text-primary mb-3">Punctual Service</h4>
            <p className="text-sm sm:text-base text-slate-600 font-public-sans">
              Timely departures and arrivals. We value your time and ensure schedules are maintained with precision.
            </p>
          </div>
          <div className="p-6 sm:p-8 border-l-4 border-l-secondary bg-white hover:-translate-y-1 transition-transform duration-300 shadow-sm" data-testid="feature-comfort-2">
            <Users className="w-10 h-10 sm:w-12 sm:h-12 text-secondary mb-4" />
            <h4 className="font-manrope font-bold text-lg sm:text-xl text-primary mb-3">Premium Electric Comfort</h4>
            <p className="text-sm sm:text-base text-slate-600 font-public-sans">
              Travel in eco-friendly BYD eMax 7 electric MPVs with spacious seating, climate control, and whisper-quiet rides.
            </p>
          </div>
        </div>

        <div className="mt-12 sm:mt-16 bg-slate-50 rounded-sm p-6 sm:p-8 lg:p-12">
          <h3 className="font-manrope font-bold text-2xl sm:text-3xl text-primary text-center mb-6 sm:mb-8">Our Premium Fleet</h3>
          <div className="max-w-4xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/c2yrkoit_Screenshot_20260114_004056_Gallery.jpg"
              alt="Hasaka BYD eMax 7 Prime EV Vehicle"
              className="rounded-sm shadow-lg w-full h-auto object-cover"
            />
          </div>
          <p className="text-center text-slate-600 font-public-sans mt-4 sm:mt-6 text-sm sm:text-base lg:text-lg px-4">
            Experience the future of intercity travel with our 100% electric BYD eMax 7 fleet
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-manrope font-bold text-2xl sm:text-3xl lg:text-4xl text-primary text-center mb-8 sm:mb-12">Get In Touch</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
            <div className="bg-white rounded-sm p-6 sm:p-8 shadow-sm border border-slate-200">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-manrope font-bold text-lg sm:text-xl text-primary mb-2">WhatsApp Bookings</h4>
                  <p className="text-sm sm:text-base text-slate-600 font-public-sans mb-3">Quick and easy mobile booking</p>
                  <a 
                    href="https://wa.me/917317676731" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-sm font-semibold hover:bg-green-700 transition-colors text-sm sm:text-base"
                    data-testid="whatsapp-button"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    <span className="break-all">+91 73176 76731</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-sm p-6 sm:p-8 shadow-sm border border-slate-200">
              <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-manrope font-bold text-lg sm:text-xl text-primary mb-2">Write To Us</h4>
                  <p className="text-sm sm:text-base text-slate-600 font-public-sans mb-3">For inquiries and support</p>
                  <a 
                    href="mailto:myev@hasakaprimeev.in" 
                    className="inline-flex items-center gap-2 bg-secondary text-white px-4 sm:px-6 py-2 sm:py-3 rounded-sm font-semibold hover:bg-secondary-600 transition-colors text-sm sm:text-base break-all"
                    data-testid="email-button"
                  >
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    myev@hasakaprimeev.in
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-primary text-white py-8 sm:py-12 px-4 sm:px-8 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 mb-6 sm:mb-8">
            <div className="text-center sm:text-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
                alt="Hasaka Tours & Travels"
                className="h-12 sm:h-16 w-auto mb-3 sm:mb-4 brightness-0 invert mx-auto sm:mx-0"
              />
              <p className="text-sm sm:text-base text-slate-300 font-public-sans">Premium Electric Intercity Travel</p>
            </div>
            
            <div className="text-center sm:text-left">
              <h3 className="font-manrope font-bold text-base sm:text-lg mb-3 sm:mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm sm:text-base text-slate-300 font-public-sans">
                <li><a href="/" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="/login" className="hover:text-white transition-colors">Login</a></li>
                <li><a href="/signup" className="hover:text-white transition-colors">Sign Up</a></li>
              </ul>
            </div>
            
            <div className="text-center sm:text-left">
              <h3 className="font-manrope font-bold text-base sm:text-lg mb-3 sm:mb-4">Contact Us</h3>
              <ul className="space-y-3 text-sm sm:text-base text-slate-300 font-public-sans">
                <li className="flex items-center gap-2 justify-center sm:justify-start flex-wrap">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <a href="https://wa.me/917317676731" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors break-all">
                    +91 73176 76731
                  </a>
                </li>
                <li className="flex items-center gap-2 justify-center sm:justify-start flex-wrap">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <a href="mailto:myev@hasakaprimeev.in" className="hover:text-white transition-colors break-all">
                    myev@hasakaprimeev.in
                  </a>
                </li>
                <li className="flex items-center gap-2 justify-center sm:justify-start">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <span>hasakaprimeev.com</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-4 sm:pt-6 text-center">
            <p className="text-slate-400 font-public-sans text-xs sm:text-sm">© 2025 Hasaka Tours & Travels. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;